import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { ImageIcon, Palette, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fileUrl } from "@/components/Certificate";
import { toast } from "sonner";

// One `kind` per upload slot on the backend.
const IMAGE_SLOTS = [
  { kind: "header", label: "Header image", field: "header_image_path", hint: "Institution seal / masthead — sits at the top of every certificate." },
  { kind: "signature_1", label: "Signature 1 (left · top)", field: "signature_1_path", hint: "PNG with transparent background works best." },
  { kind: "signature_2", label: "Signature 2 (left · bottom)", field: "signature_2_path", hint: "Optional. Leave blank to hide." },
  { kind: "seal", label: "Institution seal", field: "seal_image_path", hint: "Round embossed seal shown in the center of the footer." },
  { kind: "signature_3", label: "Signature 3 (right · top)", field: "signature_3_path", hint: "Optional." },
  { kind: "signature_4", label: "Signature 4 (right · bottom)", field: "signature_4_path", hint: "Optional." },
];

export default function CertificateDesignDialog({ institution, open, onOpenChange, onSaved }) {
  const [text1, setText1] = useState("");
  const [text2, setText2] = useState("");
  const [text3, setText3] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null); // kind currently uploading
  const [current, setCurrent] = useState(institution || null);

  useEffect(() => {
    setCurrent(institution || null);
    setText1(institution?.text_field_1 || "");
    setText2(institution?.text_field_2 || "");
    setText3(institution?.text_field_3 || "");
  }, [institution]);

  if (!current) return null;

  const handleTextSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch(`/institutions/${current.id}`, {
        text_field_1: text1,
        text_field_2: text2,
        text_field_3: text3,
      });
      setCurrent(data);
      toast.success("Certificate text saved");
      onSaved?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save text");
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    setUploading(kind);
    try {
      await api.post(`/institutions/${current.id}/assets`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Refetch so the new path shows immediately
      const { data } = await api.get(`/institutions/${current.id}`);
      setCurrent(data);
      onSaved?.(data);
      toast.success(`${kind.replace("_", " ")} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto dialog-scroll-hint" data-testid="cert-design-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Palette className="w-5 h-5 text-blue-600" /> Certificate design · {current.name}
          </DialogTitle>
          <DialogDescription>
            Configure the header, signatures, seal, and boilerplate text shown on every certificate this institution issues.
          </DialogDescription>
        </DialogHeader>

        {/* Text fields (RED) */}
        <form onSubmit={handleTextSave} className="space-y-4 pt-2">
          <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Boilerplate text</div>
          <div>
            <Label className="text-sm">Text Field 1 · Preamble / title</Label>
            <Input
              value={text1}
              onChange={(e) => setText1(e.target.value)}
              placeholder="This is to certify that"
              maxLength={300}
              className="h-11 mt-1.5"
              data-testid="design-text-1-input"
            />
          </div>
          <div>
            <Label className="text-sm">Text Field 2 · Degree conferral</Label>
            <Textarea
              value={text2}
              onChange={(e) => setText2(e.target.value)}
              placeholder="has successfully completed the requirements for the degree of"
              maxLength={300}
              rows={2}
              className="mt-1.5"
              data-testid="design-text-2-input"
            />
          </div>
          <div>
            <Label className="text-sm">Text Field 3 · Honors / signatory line (optional)</Label>
            <Input
              value={text3}
              onChange={(e) => setText3(e.target.value)}
              placeholder="with distinction · granted this __ day of ___"
              maxLength={300}
              className="h-11 mt-1.5"
              data-testid="design-text-3-input"
            />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="design-save-text-btn">
              {saving ? "Saving…" : "Save text"}
            </Button>
          </div>
        </form>

        {/* Image slots (GREEN) */}
        <div className="pt-6 mt-4 border-t border-slate-200 dark:border-slate-800">
          <div className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-3">Images</div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
            All images are optional. PNG, JPEG, or WEBP up to 8 MB. Transparent PNGs work best for signatures.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {IMAGE_SLOTS.map((slot) => {
              const existing = current[slot.field];
              const url = fileUrl(existing);
              const busy = uploading === slot.kind;
              return (
                <Card key={slot.kind} className="p-4 border-slate-200 dark:border-slate-800" data-testid={`design-slot-${slot.kind}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">{slot.label}</div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{slot.hint}</div>
                    </div>
                  </div>
                  {existing && url ? (
                    <div className="h-20 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex items-center justify-center overflow-hidden mb-2">
                      <img src={url} alt="" className="max-h-full max-w-full object-contain" />
                    </div>
                  ) : (
                    <div className="h-20 rounded-md border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 flex items-center justify-center text-slate-400 mb-2">
                      <ImageIcon className="w-5 h-5" />
                    </div>
                  )}
                  <label className="block">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => handleUpload(slot.kind, e.target.files?.[0])}
                      disabled={busy}
                      data-testid={`design-upload-${slot.kind}-input`}
                    />
                    <div className={`text-xs flex items-center justify-center gap-1.5 py-2 rounded-md border cursor-pointer transition-colors ${busy ? "border-slate-200 text-slate-400 cursor-wait" : "border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                      <Upload className="w-3 h-3" />
                      {busy ? "Uploading…" : existing ? "Replace" : "Upload"}
                    </div>
                  </label>
                </Card>
              );
            })}
          </div>
        </div>

        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="design-close-btn">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
